import { setContext } from "@apollo/client/link/context";

import { getAuthToken } from "../auth";

/**
 * Apollo auth link: attaches the monitor JWT, and knows when not to.
 *
 * Extracted from index.js by MONITOR-182 for two reasons. The first is
 * testability: nothing could exercise the link chain's first hop
 * without booting the whole app, so the defect below survived review
 * and a full MONITOR-135 regression suite.
 *
 * The second is the defect itself. This link runs FIRST in the chain
 * (`from([authLink, errorLink, httpLink])`), and it used to merge its
 * header over the top of whatever the operation had set for itself:
 *
 *     return { headers: { ...headers, authorization: `Bearer ${token}` } }
 *
 * `RefreshMonitorToken` and `ExchangeLogtoToken` both deliberately set
 * `authorization: ''` on their operation context, and both had a
 * comment asserting they therefore travel without a bearer. They did
 * not: the spread overwrote the empty string with the stale JWT.
 *
 * That is fatal rather than untidy, because /graphql is served by the
 * gateway, not monitor-api. The gateway verifies the bearer in a
 * preHandler and returns 401 INVALID_TOKEN before routing anything
 * (see graphql-gateway/src/auth/preHandler.ts). So a mid-session
 * refresh, whose whole purpose is to replace an expired JWT, went out
 * carrying that expired JWT and was rejected at the door. The
 * refresh cookie was never read. monitor-api's own `NO_AUTH_REQUIRED`
 * exemption for the refresh mutation is unreachable for the same
 * reason: the request never gets that far.
 */

/**
 * Operations that mint credentials. Their credential is the httpOnly
 * refresh cookie or a mutation argument, never the Authorization
 * header, and attaching an expired bearer to any of them turns a
 * recoverable session into a sign-out.
 *
 * Named explicitly rather than inferred: this is the invariant the
 * gateway's behaviour makes load-bearing, and it should be greppable.
 */
export const CREDENTIAL_MINTING_OPS = new Set([
  "RefreshMonitorToken",
  "ExchangeLogtoToken",
  "LoginMutation",
]);

/**
 * Remove the header rather than blanking it. The gateway branches on
 * `rawAuth.length > 0`, and monitor-api's auth-middleware logs a
 * spurious `Invalid Compact JWS` warning for every empty bearer it is
 * asked to verify.
 */
const withoutAuthorization = (headers) => {
  const { authorization, ...rest } = headers;
  return rest;
};

/**
 * @param readToken  Injected so tests can drive the link without
 *   reaching into sessionStorage. Defaults to the real accessor.
 */
export const createAuthLink = (readToken = getAuthToken) =>
  setContext((operation, prevContext) => {
    const headers = prevContext?.headers ?? {};

    if (CREDENTIAL_MINTING_OPS.has(operation?.operationName)) {
      return { headers: withoutAuthorization(headers) };
    }

    // Honour an Authorization the operation set for itself instead of
    // merging over it; an explicitly empty one means "send none".
    const explicit = headers.authorization;
    if (explicit !== undefined) {
      return { headers: explicit === "" ? withoutAuthorization(headers) : headers };
    }

    const token = readToken();
    if (!token) return { headers };
    return { headers: { ...headers, authorization: `Bearer ${token}` } };
  });

export default createAuthLink;
